import { msalConfig } from '../authConfig';
import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { getAccessToken } from './TokenService';

const backendBaseUrl = msalConfig.baseUrl;

export interface ChatMessage {
    role: string;
    content: string;
}

export interface ChatRequest {
    question: string;
    selectedCompany: { id: number; company: string };
    executionMode: 'Chat' | 'Memo Gen';
    source: string;
    memoPrompt?: string;
    memoPromptId?: number;
    files?: File[];
    chatHistory?: ChatMessage[];
    conversationId?: number;
}

export interface Citation {
    id: string;
    title: string;
    source: string;
    url: string;
    snippet: string;
    citationNumber: number;
}

export interface ChatChunkResponse {
    text: string;
    citations?: Citation[];
    isUser: boolean;
    timestamp: Date;
    isComplete: boolean;
}

export async function* sendChatMessage(
    msalInstance: IPublicClientApplication, 
    chatRequest: ChatRequest, 
    account?: AccountInfo
): AsyncGenerator<ChatChunkResponse> {
    const token = await getAccessToken(msalInstance, account);

    // Create FormData
    const formData = new FormData();
    formData.append('Prompt', chatRequest.question);
    formData.append('CompanyDataSourceId', chatRequest.selectedCompany.id.toString());
    formData.append('CompanyName', chatRequest.selectedCompany.company);
    formData.append('ExecutionMode', chatRequest.executionMode);
    formData.append('KnowledgeSource', chatRequest.source);
    
    if (chatRequest.memoPrompt) {
        formData.append('MemoPromptName', chatRequest.memoPrompt);
    }

    if (chatRequest.memoPromptId) {
        formData.append('MemoPromptId', chatRequest.memoPromptId.toString());
    }

    if (chatRequest.conversationId) {
        formData.append('ConversationId', chatRequest.conversationId.toString());
    }

    if (chatRequest.chatHistory) {
        formData.append('ChatHistory', JSON.stringify(
            chatRequest.chatHistory.map(msg => [msg.role, msg.content])
        ));
    }

    // Append files if present
    if (chatRequest.files && chatRequest.files.length > 0) {
        if (chatRequest.files.length > 5) {
            throw new Error("A maximum of 5 files are allowed.");
        }
        chatRequest.files.forEach(file => {
            formData.append('files', file);
        });
    }

    // Use fetch for streaming support
    const response = await fetch(`${backendBaseUrl}api/chat`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: formData
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
        throw new Error('No response body received');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        while (true) {
            const { value, done } = await reader.read();
            
            // Handle stream completion
            if (done) {
                if (buffer) {
                    try {
                        const parsedData = JSON.parse(buffer);
                        
                        // Handle array format response
                        if (Array.isArray(parsedData)) {
                            // API returns an array of chunks, extract text and look for citations
                            let fullText = '';
                            let citations: Citation[] | undefined = undefined;
                            
                            // Build full text from array chunks
                            parsedData.forEach((chunk: any, index: number) => {
                                if (chunk && typeof chunk === 'object') {
                                    if (chunk.text) {
                                        fullText += chunk.text;
                                    }
                                    // Look for citations in any chunk
                                    if (chunk.citations && !citations) {
                                        citations = chunk.citations;
                                    }
                                }
                            });
                            
                            // Check if there's a separate object with just citations
                            const citationObject = parsedData.find((item: any) => 
                                item && typeof item === 'object' && item.citations && !item.text
                            );
                            if (citationObject && citationObject.citations) {
                                citations = citationObject.citations;
                            }
                            
                            // Also check if citations are at the root level of the last chunk
                            const lastChunk = parsedData[parsedData.length - 1];
                            if (lastChunk && lastChunk.citations) {
                                citations = lastChunk.citations;
                            }
                            
                            console.log('Extracted citations from array:', citations);
                            
                            yield {
                                text: fullText,
                                citations: citations,
                                isUser: false,
                                timestamp: new Date(),
                                isComplete: true
                            };
                        }
                        // Check if this is the numbered object format
                        else if (typeof parsedData === 'object' && !parsedData.text && !parsedData.isUser && Object.keys(parsedData).some(key => !isNaN(parseInt(key)))) {
                            // Handle numbered object format
                            let fullText = '';
                            const keys = Object.keys(parsedData).filter(key => !isNaN(parseInt(key))).sort((a, b) => parseInt(a) - parseInt(b));
                            
                            for (const key of keys) {
                                if (parsedData[key] && parsedData[key].text) {
                                    fullText += parsedData[key].text;
                                }
                            }
                            
                            yield {
                                text: fullText,
                                citations: parsedData.citations,
                                isUser: false,
                                timestamp: new Date(),
                                isComplete: true
                            };
                        } else {
                            // Handle standard format
                            const chunk = parsedData as ChatChunkResponse;
                            yield { ...chunk, isComplete: true };
                        }
                    } catch (e) {
                        // Handle non-JSON final chunk
                        console.debug('Non-JSON final chunk, treating as plain text:', buffer);
                        yield {
                            text: buffer.trim(),
                            citations: undefined,
                            isUser: false,
                            timestamp: new Date(),
                            isComplete: true
                        };
                    }
                }
                break;
            }

            // Decode and process new chunks
            buffer += decoder.decode(value, { stream: true });
            
            // Split by newline and process each complete chunk
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete chunk in buffer

            for (const line of lines) {
                if (line.trim()) {
                    try {
                        const parsedData = JSON.parse(line);
                        console.log('Parsed data from API:', parsedData);
                        
                        // Check if this is the numbered object format
                        if (typeof parsedData === 'object' && !parsedData.text && !parsedData.isUser && Object.keys(parsedData).some(key => !isNaN(parseInt(key)))) {
                            // Handle numbered object format like { "0": {"text": ""}, "1": {"text": "I'm"}, ... }
                            let fullText = '';
                            const keys = Object.keys(parsedData).filter(key => !isNaN(parseInt(key))).sort((a, b) => parseInt(a) - parseInt(b));
                            
                            for (const key of keys) {
                                if (parsedData[key] && parsedData[key].text) {
                                    fullText += parsedData[key].text;
                                }
                            }
                            
                            yield {
                                text: fullText,
                                citations: parsedData.citations,
                                isUser: false,
                                timestamp: new Date(),
                                isComplete: parsedData.isComplete || false
                            };
                        } else {
                            // Handle standard format
                            const chunk = parsedData as ChatChunkResponse;
                            yield { ...chunk, isComplete: false };
                        }
                    } catch (e) {
                        // If JSON parsing fails, treat the line as plain text
                        console.debug('Non-JSON chunk received, treating as plain text:', line);
                        yield {
                            text: line.trim(),
                            citations: undefined,
                            isUser: false,
                            timestamp: new Date(),
                            isComplete: false
                        };
                    }
                }
            }
        }
    } finally {
        reader.cancel();
    }
}

